import {
  ReactElement,
  forwardRef,
  isValidElement,
  ComponentPropsWithoutRef,
  useId,
  useRef,
} from "react";
import cx from "classnames";
import { Spinner } from "../Spinner";
import textFieldStyles from "../TextField/TextField.module.scss";
import numberFieldStyles from "./NumberField.module.scss";
import { Helper, HelperProps } from "../../internal/components";
import { warnDeprecatedErrorUsage } from "../FieldMessage";
import { LayoutUtilProps, Size, DataTrackingId } from "../../types";
import { useMergeRefs, useTrackingId } from "../../hooks";
import { Button } from "../Button";
import { Icon } from "../Icon";
import IconAdd from "@servicetitan/hammer-icon/mdi/round/add.svg";
import IconRemove from "@servicetitan/hammer-icon/mdi/round/remove.svg";
import {
  useFocusWithin,
  useLayoutPropsUtil,
  useNumberField,
} from "../../internal/hooks";
import {
  childrenToString,
  stripInlineMarkdown,
  warnOnce,
} from "../../internal/functions";
import { FieldLabel } from "../FieldLabel";
import { FieldLabelProps } from "../../internal/types";

/**
 * Props for the NumberField component.
 * @extends Omit<ComponentPropsWithoutRef<"input">, "size" | "prefix" | "type" | "inputMode" | "value" | "defaultValue" | "onFocus" | "onBlur" | "onChange">
 * @extends LayoutUtilProps
 * @extends FieldLabelProps
 * @extends DataTrackingId
 */
export type NumberFieldProps = Omit<
  ComponentPropsWithoutRef<"input">,
  | "size"
  | "prefix"
  | "type"
  | "inputMode"
  | "value"
  | "defaultValue"
  | "onFocus"
  | "onBlur"
  | "onChange"
> &
  LayoutUtilProps &
  FieldLabelProps & {
    /**
     * Error state for the field. Pass `true` to indicate error styling without a message.
     * Pass a string, string[], or ReactElement (deprecated) for error messages.
     */
    error?: boolean | string | ReactElement | string[];
    // TODO(v4.0.0): make label required
    /**
     * Label for the field.
     * Omitting `label` is deprecated — it will be required in v4.0.0. Use `hideLabel` to visually hide it.
     */
    label?: string;
    /**
     * When `true`, hides the visible label and moves it to `aria-label` on the input.
     * Requires `label` to be set — the label is always needed for accessibility.
     * @default false
     */
    hideLabel?: boolean;
    prefix?: string;
    suffix?: string;
    size?: Extract<Size, "small" | "medium" | "large">;
    description?: HelperProps["description"];
    /**
     * @deprecated No longer used. Error messages always use `aria-live="assertive"`.
     */
    errorAriaLive?: HelperProps["errorAriaLive"];
    hint?: HelperProps["hint"];
    /**
     * Warning message(s) to display. Supports a single string or an array of strings.
     */
    warning?: string | string[];
    loading?: boolean;
    disabled?: boolean;
    minValue?: number;
    maxValue?: number;
    maximumFractionDigits?: number;
    minimumFractionDigits?: number;
    step?: number;
    value?: number | null;
    defaultValue?: number | null;
    onChange?: (value: number | null) => void;
    onFocus?: (event: React.FocusEvent) => void;
    onBlur?: (event: React.FocusEvent) => void;
    hideControls?: boolean;
  } & DataTrackingId;

export const NumberField = forwardRef<HTMLInputElement, NumberFieldProps>(
  (props, ref) => {
    const { layoutStyles, componentProps } = useLayoutPropsUtil(props);
    const {
      className,
      error,
      errorAriaLive: _errorAriaLive,
      defaultValue: defaultValueProp,
      value: valueProp,
      label,
      moreInfo,
      prefix,
      suffix,
      size,
      required,
      onChange: onChangeProp,
      onFocus: onFocusProp,
      onBlur: onBlurProp,
      id: idProp,
      description,
      hint,
      warning,
      loading,
      disabled,
      readOnly,
      labelProps,
      style,
      placeholder,
      minValue,
      maxValue,
      maximumFractionDigits = 0,
      minimumFractionDigits = 0,
      step = 1,
      hideControls = false,
      hideLabel = false,
      ...rest
    } = componentProps;
    const numberFieldRef = useRef<HTMLDivElement>(null);

    const { props: wrapperProps } = useFocusWithin({
      onFocus: (e) => {
        onFocusProp?.(e);
      },
      onBlur: (e) => {
        onBlurProp?.(e);
      },
    });

    const { inputProps, inputRef, incrementButtonProps, decrementButtonProps } =
      useNumberField({
        minValue,
        maxValue,
        maximumFractionDigits,
        minimumFractionDigits,
        step,
        value: valueProp,
        defaultValue: defaultValueProp,
        onChange: onChangeProp,
        label,
      });

    const combinedInputRef = useMergeRefs([numberFieldRef, inputRef, ref]);

    const labelUid = useId();
    const id = idProp ?? labelUid;
    const prefixUid = useId();
    const suffixUid = useId();
    const helperUid = useId();
    const moreInfoUid = useId();

    warnDeprecatedErrorUsage("NumberField", error);

    // TODO(v4.0.0): remove once label is required
    if (label === undefined) {
      warnOnce(
        "NumberField: label will be required in v4.0.0. Use hideLabel to visually hide the label.",
      );
    }

    const errorMessages =
      typeof error === "boolean" || error === undefined ? undefined : error;

    const hasHelperText = hint || errorMessages || warning || description;

    const ariaDescribedBy =
      [hasHelperText && helperUid, moreInfo && moreInfoUid]
        .filter(Boolean)
        .join(" ") || undefined;

    const styleCombined = { ...style, ...layoutStyles };

    const data = {
      label: childrenToString(props.label),
      prefix: childrenToString(props.prefix),
      suffix: childrenToString(props.suffix),
      description: childrenToString(props.description),
      size: props.size,
      minValue: props.minValue,
      maxValue: props.maxValue,
      step: props.step,
      maximumFractionDigits: props.maximumFractionDigits,
      minimumFractionDigits: props.minimumFractionDigits,
    };

    const trackingId = useTrackingId({
      name: "NumberField",
      data,
      hasOverride: !!props["data-tracking-id"],
    });

    return (
      <div
        className={cx(textFieldStyles["textfield"], className)}
        data-anv="number-field"
        style={styleCombined}
        {...wrapperProps}
      >
        {/* TODO(v4.0.0): simplify to !hideLabel once label is required */}
        {label && !hideLabel && (
          <FieldLabel
            moreInfo={moreInfo}
            moreInfoId={moreInfo ? moreInfoUid : undefined}
            required={required}
            htmlFor={id}
            {...labelProps}
          >
            {label}
          </FieldLabel>
        )}
        <div
          className={cx(textFieldStyles["input-wrapper"], {
            [textFieldStyles["small"]]: size === "small",
            [textFieldStyles["large"]]: size === "large",
            [numberFieldStyles["small"]]: size === "small",
            [numberFieldStyles["large"]]: size === "large",
          })}
          role="presentation"
          onClick={() => numberFieldRef.current?.focus()}
        >
          {prefix ? (
            <div
              aria-hidden
              className={textFieldStyles["prefix"]}
              id={`prefix${prefixUid}`}
            >
              {prefix}
            </div>
          ) : null}
          <input
            id={id}
            className={cx(textFieldStyles["input"], {
              [textFieldStyles["error"]]: error,
            })}
            data-tracking-id={trackingId}
            {...rest}
            {...inputProps}
            required={required}
            placeholder={placeholder}
            readOnly={readOnly}
            disabled={disabled}
            ref={combinedInputRef}
            aria-label={
              // TODO(v4.0.0): remove typeof check — label will always be string
              hideLabel && typeof label === "string"
                ? stripInlineMarkdown(label)
                : label
                  ? undefined
                  : placeholder
            }
            aria-describedby={ariaDescribedBy}
            aria-invalid={error ? !!error : undefined}
          />
          {loading ? (
            <div
              className={textFieldStyles["loading-spinner-wrapper"]}
              data-testid="spinner"
            >
              <Spinner size="small" />
            </div>
          ) : null}
          {suffix ? (
            <div
              className={textFieldStyles["suffix"]}
              aria-hidden
              id={`suffix${suffixUid}`}
            >
              {suffix}
            </div>
          ) : null}
          {!hideControls && !disabled && !readOnly && (
            <div className={numberFieldStyles["stepper-buttons"]}>
              <Button
                appearance="secondary"
                size="small"
                {...decrementButtonProps}
              >
                <Icon svg={IconRemove} />
              </Button>
              <Button
                appearance="secondary"
                size="small"
                {...incrementButtonProps}
              >
                <Icon svg={IconAdd} />
              </Button>
            </div>
          )}
        </div>
        {hasHelperText ? (
          <Helper
            id={helperUid}
            hint={hint}
            showCounter={false}
            error={
              typeof errorMessages === "string" || Array.isArray(errorMessages)
                ? errorMessages
                : undefined
            }
            warning={warning}
            errorMessage={
              isValidElement(errorMessages) ? errorMessages : undefined
            }
            description={description}
          />
        ) : null}
      </div>
    );
  },
);

NumberField.displayName = "NumberField";
